import { ROOT_TEAM_ID } from '@scio3/shared-types';

/**
 * Shared folder security utilities — single source of truth.
 * Used by all folder row/card components to determine shared state.
 */

/** Returns true if folder has non-default security settings (not just "everyone on team") */
export function isFolderShared(
  folder:
    | { securities?: Array<{ userId: string }>; securityTeams?: string[] | null }
    | null
    | undefined,
  folderLevelSecurity: boolean,
): boolean {
  if (!folder || !folderLevelSecurity) return false;
  return (
    (folder.securities ?? []).some((s) => s.userId !== '0') ||
    (folder.securityTeams != null && folder.securityTeams.length > 0)
  );
}

/**
 * Can the user write into the destination a folder picker is pointing at (Move To, "save to
 * folder", New Folder inside the picker)? scio3#2550.
 *
 * `canWriteHere` must be the FOLDER-AWARE answer — `usePermissions({ parentFolder: target })`
 * — so a View-only folder or another team's folder the user is not admin of says no. Inside a
 * folder whose object has not loaded yet, that answer degrades to role-only, so the target
 * stays blocked until it resolves; root (the active team's Home) needs no folder object, and
 * root Owner / Super Admin bypass folder security everywhere.
 */
export function canWriteFolderPickerTarget({
  canWriteHere,
  isRoot,
  isTargetPending,
  isRootSuperAdmin,
}: {
  canWriteHere: boolean;
  isRoot: boolean;
  isTargetPending: boolean;
  isRootSuperAdmin: boolean;
}): boolean {
  if (!canWriteHere) return false;
  if (isRoot || isRootSuperAdmin) return true;
  return !isTargetPending;
}

/**
 * The owning team of a LOADED folder. Legacy Default-team folders have no `teamId` in Mongo
 * (the API returns null); the backend reads a missing folder teamId as the root team
 * (`teamId || TEAM_TYPES.ROOT`, `_getTeamIdSelector` → `{ $in: [null, '1'] }`). Only call this
 * on a folder object that was actually fetched — never to invent a team for a missing folder.
 */
export function folderOwnerTeamId(folder: { teamId?: string | null }): string {
  return folder.teamId || ROOT_TEAM_ID;
}

/** Copy of a loaded folder with its owning team made explicit (see folderOwnerTeamId). */
export function withOwnerTeamId<T extends { teamId?: string | null }>(
  folder: T,
): T & { teamId: string } {
  return { ...folder, teamId: folderOwnerTeamId(folder) };
}
